/**
 * Classe Song
 *
 * @author Fabien Bellanger
 */
export default class Song
{
    /**
     * Constructeur
     *
     * @author Fabien Bellanger
     */
    constructor()
    {
        // Variables
        // ---------
        this.init(Song.getEmptySong());
    }

    /**
     * Initialisation d'une chanson
     *
     * @static
     * @author Fabien Bellanger
     * @param {Song} song Chanson
     */
    init(song)
    {
        this.id = song.id;
        this.title = song.title;
        this.author = song.author;
        this.album = song.album;
        this.key = song.key;
        this.capo = song.capo;
        this.tempo = song.tempo;
        this.timeSignature = song.timeSignature;
        this.parts = song.parts;
        this.comment = song.comment;
        this.displayMeasureNumber = song.displayMeasureNumber;
    }

    /**
     * Retourne une chanson vide
     *
     * @static
     * @author Fabien Bellanger
     * @return {Song}
     */
    static getEmptySong()
    {
        return {
            id:                   0,
            title:                '',
            author:               '',
            album:                '',
            key:                  '',
            capo:                 0,
            tempo:                0,
            timeSignature:        '',
            parts:                [Song.getEmptyPart()],
            comment:              '',
            displayMeasureNumber: false,
        };
    }
  
    /**
     * Initialisation d'un objet Song à partir de la session
     *
     * @static
     * @author Fabien Bellanger
     * @return {Song}
     */
    static getFromSession()
    {
        let song = new Song();

        try
        {
            const songJson = JSON.parse(localStorage.getItem('song'));

            if (songJson !== null)
            {
                song.id = (songJson.id) ? songJson.id : 0;
                song.title = (songJson.title) ? songJson.title : '';
                song.author = (songJson.author) ? songJson.author : '';
                song.album = (songJson.album) ? songJson.album : '';
                song.key = (songJson.key) ? songJson.key : '';
                song.capo = (songJson.capo) ? +songJson.capo : 0;
                song.tempo = (songJson.tempo) ? +songJson.tempo : 0;
                song.timeSignature = (songJson.timeSignature) ? +songJson.timeSignature : '';
                song.parts = (songJson.parts) ? songJson.parts : [Song.getEmptyPart()];
                song.comment = (songJson.comment) ? songJson.comment : '';
                song.displayMeasureNumber = (songJson.displayMeasureNumber) ? songJson.displayMeasureNumber : false;
            }
            
            return song;
        }
        catch (exception)
        {
            console.error(exception);
        }

        return song;
    }

    /**
     * Retourne une partie vide
     *
     * @static
     * @author Fabien Bellanger
     * @return {Object}
     */
    static getEmptyPart()
    {
        return {
            name:     '',
            measures: [],
        };
    }
  
    /**
     * Enregistrement d'un objet Song en session
     *
     * @static
     * @author Fabien Bellanger
     * @param {Song} song Chanson
     */
    static saveInSession(song)
    {
        localStorage.setItem('song', JSON.stringify(song));
    }

    /**
     * Ajout d'une partie
     *
     * @author Fabien Bellanger
     * @return {Number} Indice de la partie ajoutée
     */
    addPart()
    {
        const index = this.parts.length;

        this.parts.push(Song.getEmptyPart());

        return index;
    }

    /**
     * Mise à jour d'une mesure
     *
     * @author Fabien Bellanger
     * @param {Number} partIndex            Indice de la partie
     * @param {Number} measureIndex         Indice de la mesure dans la partie
     * @param {Object} chord                Accord
     * @param {Number} selectedPartIndex    Indice de la partie sélectionnée
     */
    editMeasure(partIndex, measureIndex, chord, selectedPartIndex)
    {
        if (chord !== null)
        {
            if (this.parts[partIndex] !== undefined)
            {
                // La partie existe déjà
                // ---------------------
                if (measureIndex !== -1 && this.parts[partIndex].measures[measureIndex] !== undefined)
                {
                    // Modification de la mesure
                    // -------------------------
                    this.parts[partIndex].measures.splice(measureIndex, 1, chord);
                }
                else
                {
                    // Ajout à la fin de la partie
                    // ---------------------------
                    this.parts[partIndex].measures.push(chord);
                }
            }
            else
            {
                // La partie n'est pas trouvée dans le tableau
                // -------------------------------------------
                if (this.parts.length === 0)
                {
                    // Pas de partie, on en crée une
                    // -----------------------------
                    let part = {
                        name:     '',
                        measures: [chord],
                    };

                    this.parts.push(part);
                }
                else
                {
                    // Sinon on prend la partie selectionnée
                    // -------------------------------------

                    // La partie existe déjà
                    // ---------------------
                    if (measureIndex !== -1 && this.parts[selectedPartIndex].measures[measureIndex] !== undefined)
                    {
                        // Modification de la mesure
                        // -------------------------
                        this.parts[selectedPartIndex].measures.splice(measureIndex, 1, chord);
                    }
                    else
                    {
                        // Ajout à la fin de la partie
                        // ---------------------------
                        this.parts[selectedPartIndex].measures.push(chord);
                    }
                }
            }
        }
    }

    /**
     * Modification du nom de la partie sélectionnée
     *
     * @author Fabien Bellanger
     * @param {String} name                 Nom de la partie
     * @param {Number} selectedPartIndex    Indice de la partie sélectionnée
     */
    updatePartName(name, selectedPartIndex)
    {
        if (this.parts[selectedPartIndex] !== undefined)
        {
            this.parts[selectedPartIndex].name = name;
        }
        else if (this.parts.length === 0)
        {
            // Si aucune partie de créer, on en crée une
            // -----------------------------------------
            this.parts.push({
                name:     name,
                measures: [],
            });
        }
    }

    /**
     * Suppression d'une mesure
     *
     * @author Fabien Bellanger
     * @param {Number} partIndex        Indice de la partie
     * @param {Number} measureIndex     Indice de la mesure dans la partie
     * @return {Boolean}
     */
    deleteMeasure(partIndex, measureIndex)
    {
        if (this.parts[partIndex] !== undefined && this.parts[partIndex].measures[measureIndex] !== undefined)
        {
            const element = this.parts[partIndex].measures.splice(measureIndex, 1);

            return (element.length === 1);
        }
        else
        {
            return false;
        }
    }

    /**
     * Suppression d'une partie
     *
     * @author Fabien Bellanger
     * @param {Number} partIndex  Indice de la partie
     * @return {Boolean}
     */
    deletePart(partIndex)
    {
        if (this.parts[partIndex] !== undefined)
        {
            const element = this.parts.splice(partIndex, 1);

            return (element.length === 1);
        }
        else
        {
            return false;
        }
    }

    /**
     * La chanson peut-elle être enregistrée ?
     *
     * @static
     * @author Fabien Bellanger
     * @return {Boolean}
     */
    static canBeSaved(song)
    {
        return song.title !== '';
    }

    /**
     * Retourne la liste des chansons
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getSongsList()
    {
        try
        {
            const songsJson = JSON.parse(localStorage.getItem('songsList'));
            let songs       = [];

            if (songsJson !== null)
            {
                songs = songsJson;
            }
            
            return songs;
        }
        catch (exception)
        {
            console.error(exception);
        }
    }

    /**
     * Liste des tonalités
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getKeysList()
    {
        return [
            'C', 'Cm', 'C♯', 'C♯m', 'C♭',
            'D', 'Dm', 'D♯m', 'D♭',
            'E', 'E♭', 'Em', 'E♭m',
            'F', 'F♯', 'Fm', 'F♯m',
            'G', 'G♭', 'Gm', 'G♯m',
            'A', 'A♭', 'Am', 'A♯m', 'A♭m',
            'B', 'B♭', 'Bm', 'B♭m',
        ];
    }

    /**
     * Liste des chiffrages
     *
     * @static
     * @author Fabien Bellanger
     * @return {Array}
     */
    static getTimeSignaturesList()
    {
        return [
            '4/4', '2/4', '3/4',
            '12/8', '9/8', '6/8',
            '4/2', '3/2', '2/2',
        ];
    }
};
